import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BookOpen, ArrowUpRight } from "lucide-react";
import api, { API, formatApiErrorDetail, resolveImg } from "@/lib/api";
import { toast } from "@/lib/toast";

const TOOLS = [
  { id: "borrador", label: "📝 Generar borrador completo" },
  { id: "mejorar", label: "✨ Mejorar redacción" },
  { id: "titulares", label: "🎯 Sugerir titulares" },
  { id: "resumen", label: "📋 Resumen ejecutivo" },
  { id: "ficha", label: "⚖ Convertir a ficha legislativa" },
];

export default function Redactor() {
  const [params, setParams] = useSearchParams();
  const articleId = params.get("id");
  const [id, setId] = useState(articleId);
  const [title, setTitle] = useState("");
  const [dek, setDek] = useState("");
  const [loading, setLoading] = useState(!!articleId);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedStatus, setSavedStatus] = useState("");
  const savingRef = useRef(false);
  const createKeyRef = useRef(crypto.randomUUID());
  const [body, setBody] = useState("");
  const [aiOut, setAiOut] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [tagsStr, setTagsStr] = useState("");
  const [image, setImage] = useState("");
  const [sources, setSources] = useState([]);
  const [imgQuery, setImgQuery] = useState("");
  const [imgResults, setImgResults] = useState([]);
  const [imgLoading, setImgLoading] = useState(false);
  const outRef = useRef(null);
  const aiTextRef = useRef("");

  useEffect(() => {
    let active = true;
    setLoadError(""); setAiOut(""); aiTextRef.current = "";
    if (articleId) {
      setLoading(true);
      api.get(`/articles/${articleId}`).then((r) => {
        if (!active) return;
        setId(articleId); setSources(r.data.editorial_sources || []);
        setTitle(r.data.title); setBody(r.data.body || r.data.dek || "");
        setDek(r.data.dek || ""); setSavedStatus(r.data.status);
        setTagsStr((r.data.tags || []).join(", "));
        setImage(r.data.image || "");
      }).catch(() => { if (active) setLoadError("No se pudo cargar el artículo. No se guardarán cambios sobre un documento sin cargar."); })
        .finally(() => { if (active) setLoading(false); });
    } else {
      setId(null); setTitle(""); setBody(""); setDek(""); setSources([]);
      setTagsStr(""); setImage(""); setSavedStatus(""); setLoading(false);
      createKeyRef.current = crypto.randomUUID();
    }
    return () => { active = false; };
  }, [articleId]);

  const buscarImagenes = async () => {
    const q = imgQuery.trim() || title;
    if (!q) return toast("Escribe qué imagen buscar");
    setImgLoading(true);
    try {
      const { data } = await api.post("/images/search", { query: q });
      setImgResults(data.results || []);
      if (!data.results?.length) toast("Sin resultados, intenta otra búsqueda o genera con IA");
    } catch (e) {
      toast("Error: " + (formatApiErrorDetail(e.response?.data?.detail) || e.message));
    } finally { setImgLoading(false); }
  };

  const subirImagen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast("La imagen debe pesar máximo 5 MB");
    setImgLoading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/images/upload", form);
      setImage(data.url); setImgResults([]);
      toast("✓ Imagen cargada");
    } catch (ex) {
      toast("Error: " + (formatApiErrorDetail(ex.response?.data?.detail) || ex.message));
    } finally { setImgLoading(false); }
  };

  const generarImagen = async () => {
    const prompt = imgQuery.trim() || `Ilustración editorial para nota periodística: ${title}. Estilo sobrio, paleta azul marino y verde, sin texto.`;
    setImgLoading(true);
    try {
      const { data } = await api.post("/images/generate", { prompt });
      setImage(data.url);
      setImgResults([]);
      toast("✓ Imagen generada con Magnific");
    } catch (e) {
      toast("Error: " + (formatApiErrorDetail(e.response?.data?.detail) || e.message));
    } finally { setImgLoading(false); }
  };

  const runTool = async (tool) => {
    if (streaming) return;
    setStreaming(true); setAiOut(""); aiTextRef.current = "";
    try {
      const resp = await fetch(`${API}/ai/redactor`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(localStorage.getItem("legis_token") ? { Authorization: `Bearer ${localStorage.getItem("legis_token")}` } : {}),
        },
        body: JSON.stringify({ tool, title, body }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const ev = JSON.parse(line.slice(6));
          if (ev.delta) {
            aiTextRef.current += ev.delta;
            setAiOut(aiTextRef.current);
            if (outRef.current) outRef.current.scrollTop = outRef.current.scrollHeight;
          }
          if (ev.error) toast("Error de IA: " + ev.error);
        }
      }
    } catch (e) {
      toast("Error de IA: " + e.message);
    } finally { setStreaming(false); }
  };

  const insertar = () => {
    if (!aiTextRef.current) return toast("Genera primero contenido con la IA");
    setBody((b) => [b.trimEnd(), aiTextRef.current.trim()].filter(Boolean).join("\n\n"));
    toast("✓ Contenido insertado en el editor");
  };

  const save = async (status, msg) => {
    if (savingRef.current || loading || loadError || streaming) return;
    if (title.trim().length < 3) return toast("Escribe un titular de al menos 3 caracteres");
    if (!body.trim()) return toast("Agrega el contenido del artículo antes de guardarlo");
    savingRef.current = true; setSaving(true);
    const tags = tagsStr.split(",").map((t) => t.trim()).filter(Boolean);
    try {
      if (id) {
        await api.put(`/articles/${id}`, { title: title.trim(), body, dek, status, tags, image });
      } else {
        const { data } = await api.post("/articles", { title: title.trim(), body, status, tags, image, dek: dek || body.split("\n")[0].slice(0, 220), client_request_id: createKeyRef.current });
        setId(data.id);
        setParams({ id: data.id }, { replace: true });
      }
      setSavedStatus(status);
      toast(msg);
    } catch (e) {
      toast("Error: " + (formatApiErrorDetail(e.response?.data?.detail) || e.message));
    } finally { savingRef.current = false; setSaving(false); }
  };

  return (
    <div data-testid="redactor-page">
      <h1 className="page">Redactor con IA <span className="ai-badge">✦ Asistente editorial</span></h1>
      <p className="pagesub">Redacta notas y análisis con apoyo de inteligencia artificial: borradores, mejora de estilo, titulares y resúmenes.</p>
      {loading && <p role="status" data-testid="editor-loading">Cargando artículo…</p>}
      {loadError && <p role="alert" data-testid="editor-load-error">{loadError}</p>}
      {savedStatus && <p data-testid="editor-saved-status">Estado guardado: {savedStatus}{savedStatus === "publicado" && <Link style={{ marginLeft: 12 }} to={`/nota/${id}`} target="_blank" data-testid="editor-view-published">Ver artículo publicado ↗</Link>}</p>}
      <div className="redactor-library-entry"><BookOpen size={20} /><span>Un buen artículo comienza con una fuente.</span><Link to="/admin/biblioteca" data-testid="redactor-library-link">Generar desde documentos<ArrowUpRight size={16} /></Link></div>
      {!!sources.length && <details className="editor-source-details" open><summary data-testid="editor-sources-toggle">Fuentes consultadas · {sources.length} pasajes</summary><div className="editor-source-links">{sources.map(s => <Link key={`${s.id}-${s.number}`} to={s.id.startsWith("doc-") ? `/admin/biblioteca/${s.id}?pagina=${s.page || 1}` : `/sintesis/${s.source_synthesis_id || s.id}`} target="_blank" data-testid={`editor-source-${s.number}`}>[{s.number}] {s.title}{s.page ? ` · ${s.page_label || "página"} ${s.page}` : ""}<ArrowUpRight size={14} /></Link>)}</div></details>}
      <div className="editor-grid">
        <div className="panel editor">
          <input className="title" value={title} disabled={loading || saving || !!loadError} maxLength={250} onChange={(e) => setTitle(e.target.value)} placeholder="Titular del artículo…" data-testid="editor-title-input" />
          <label className="ag-field" htmlFor="editor-dek-input"><span>Extracto</span><input id="editor-dek-input" data-testid="editor-dek-input" maxLength={1000} value={dek} disabled={loading || saving || !!loadError} onChange={e => setDek(e.target.value)} /></label>
          <textarea value={body} disabled={loading || saving || !!loadError} maxLength={200000} onChange={(e) => setBody(e.target.value)} placeholder="Escribe aquí o genera un borrador con IA…" data-testid="editor-body-textarea" />
          <div style={{ marginTop: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 800, color: "var(--gray)", textTransform: "uppercase", letterSpacing: ".06em", display: "block", marginBottom: 6 }}>Etiquetas (separadas por coma)</label>
            <input value={tagsStr} onChange={(e) => setTagsStr(e.target.value)} placeholder="Ej: Trabajo, Ley Antigolondrina, Empresas"
              style={{ width: "100%", padding: "11px 14px", border: "1.5px solid #e4e8ec", borderRadius: 6 }} data-testid="editor-tags-input" />
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
            <button className="btn btn-green" disabled={saving || loading || streaming || !!loadError} onClick={() => save("borrador", "✓ Artículo guardado como borrador")} data-testid="save-draft-button">{saving ? "Guardando…" : "Guardar borrador"}</button>
            <button className="btn btn-navy" disabled={saving || loading || streaming || !!loadError} onClick={() => save("revision", "✓ Enviado a revisión editorial")} data-testid="send-review-button">Enviar a revisión</button>
            <button className="btn btn-ghost" disabled={saving || loading || streaming || !!loadError} onClick={() => save("publicado", "✓ Artículo publicado en el sitio")} data-testid="publish-article-button">Publicar</button>
          </div>
        </div>
        <div>
          <div className="panel">
            <h2>✦ Herramientas de IA</h2>
            <div className="ai-tools">
              {TOOLS.map((t) => (
                <button key={t.id} className="btn btn-ai" onClick={() => runTool(t.id)} disabled={streaming || loading || saving || !!loadError} data-testid={`ai-tool-${t.id}`}>{t.label}</button>
              ))}
            </div>
          </div>
          <div className="panel">
            <h2>🖼 Imagen del artículo <span className="ai-badge">Magnific</span></h2>
            {image && (
              <div style={{ marginBottom: 12, position: "relative" }}>
                <img src={resolveImg(image)} alt="Imagen del artículo" style={{ width: "100%", borderRadius: 8, display: "block" }} data-testid="article-image-preview" />
                <button className="btn btn-ghost sm" style={{ marginTop: 8 }} onClick={() => setImage("")} data-testid="remove-image-button">✕ Quitar imagen</button>
              </div>
            )}
            <input value={imgQuery} onChange={(e) => setImgQuery(e.target.value)} placeholder="Describe o busca la imagen…"
              style={{ width: "100%", padding: "10px 12px", border: "1.5px solid #e4e8ec", borderRadius: 6, marginBottom: 10 }} data-testid="image-query-input" />
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button className="btn btn-ghost sm" onClick={buscarImagenes} disabled={imgLoading} data-testid="search-image-button">
                {imgLoading ? "…" : "🔎 Buscar stock"}
              </button>
              <button className="btn btn-ai sm" onClick={generarImagen} disabled={imgLoading} data-testid="generate-image-button">
                {imgLoading ? "✦ Procesando…" : "✦ Generar con IA"}
              </button>
              <label className="btn btn-ghost sm" style={{ cursor: imgLoading ? "default" : "pointer" }} data-testid="upload-image-button">
                ⬆ Subir imagen
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={subirImagen} disabled={imgLoading} hidden data-testid="upload-image-input" />
              </label>
            </div>
            {imgResults.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginTop: 12 }} data-testid="image-results-grid">
                {imgResults.map((r) => (
                  <button key={r.id} onClick={() => { setImage(r.url); setImgResults([]); toast("✓ Imagen seleccionada"); }}
                    style={{ border: "2px solid #e4e8ec", borderRadius: 6, overflow: "hidden", padding: 0, background: "none", aspectRatio: "1" }}
                    title={r.title} data-testid={`image-result-${r.id}`}>
                    <img src={r.url} alt={r.title} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="panel">
            <h2>Salida del asistente</h2>
            <div className="ai-out" ref={outRef} data-testid="ai-output">
              {aiOut || (streaming ? "" : "Selecciona una herramienta de IA para comenzar…")}
              {streaming && <span className="cursor" />}
            </div>
            <button className="btn btn-ghost sm" style={{ marginTop: 10 }} disabled={!aiOut || streaming || loading || saving || !!loadError} onClick={insertar} data-testid="insert-ai-button">⤵ Insertar en el editor</button>
          </div>
        </div>
      </div>
    </div>
  );
}
